import { useState } from "react";
import { Shield } from "lucide-react";
import { cn } from "@/lib/utils";

// Public web domain for each carrier, used to pull a real logo. Government
// programs / self-pay have no brand logo to fetch, so they're left out and just
// render the shield badge.
//
// Extracted from InsuranceSearchSection so the landing carousel and the
// insurance picker dialog render identical logos from one list.
const INSURANCE_DOMAINS: Record<string, string> = {
  bcbs: "bcbs.com",
  aetna: "aetna.com",
  uhc: "uhc.com",
  cigna: "cigna.com",
  humana: "humana.com",
  kaiser: "kaiserpermanente.org",
  anthem: "anthem.com",
  molina: "molinahealthcare.com",
  centene: "centene.com",
  ambetter: "ambetterhealth.com",
  oscar: "hioscar.com",
  wellcare: "wellcare.com",
  amerigroup: "amerigroup.com",
  fidelis: "fideliscare.org",
  healthnet: "healthnet.com",
  horizon: "horizonblue.com",
  highmark: "highmark.com",
  independence: "ibx.com",
  carefirst: "carefirst.com",
  bsca: "blueshieldca.com",
  premera: "premera.com",
  regence: "regence.com",
  emblem: "emblemhealth.com",
  metroplus: "metroplus.org",
  healthfirst: "healthfirst.org",
  buckeye: "buckeyehealthplan.com",
  peachstate: "pshpgeorgia.com",
  superior: "superiorhealthplan.com",
  caresource: "caresource.com",
  geha: "geha.com",
  mutual: "mutualofomaha.com",
  bright: "brighthealthcare.com",
  friday: "fridayhealthplans.com",
  sutter: "sutterhealthplus.org",
  providence: "providencehealthplan.com",
  uphp: "upmchealthplan.com",
  harvard: "harvardpilgrim.org",
  tufts: "tuftshealthplan.com",
  cdphp: "cdphp.com",
  mvp: "mvphealthcare.com",
  selecthealth: "selecthealth.org",
  scott: "swhp.org",
  bswh: "bswhealth.com",
  medica: "medica.com",
  wellpoint: "wellpoint.com",
  moda: "modahealth.com",
  wellmark: "wellmark.com",
};

/**
 * Carrier logo with a colored shield fallback. Falls back whenever the carrier
 * has no known domain or the fetch fails — the ~110 carriers imported from CMS
 * mostly have no entry in INSURANCE_DOMAINS, so the fallback is the common
 * path, not the exception.
 */
export function InsuranceLogo({
  id,
  color,
  className,
}: {
  id: string;
  color: string;
  className?: string;
}) {
  const domain = INSURANCE_DOMAINS[id];
  const [failed, setFailed] = useState(!domain);

  if (failed) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-md text-white shrink-0",
          className ?? "h-11 w-11",
        )}
        style={{ backgroundColor: color }}
        aria-hidden
      >
        <Shield className="h-5 w-5" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-md bg-white border border-border shrink-0 p-1.5 overflow-hidden",
        className ?? "h-11 w-11",
      )}
    >
      {/* Was logo.clearbit.com, which no longer resolves at all — Clearbit's
          free Logo API was discontinued, so every carrier had silently been
          rendering the shield fallback. Google's favicon service needs no key
          and returns a PNG for every domain here. */}
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
        alt=""
        aria-hidden
        loading="lazy"
        className="h-full w-full object-contain"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
